export type SegmentPriorityClass = "strat" | "grow" | "defend" | "other"

export type SegmentRunStatus = "ok" | "needs-review"

export type SegmentAnalysisRole = "admin" | "owner" | "viewer"

/** Top-level portfolio view: segment cards/charts, the company directory, or the connections graph. */
export type SegmentAnalysisView = "portfolio" | "companies" | "map"

/** Which portfolio graph to render — hygiene keeps bubble only (#2421). */
export type SegmentChartKind = "bubble" | "scores" | "treemap" | "addressable" | "trend"

/** Selectable graphs offered in the Customize popover. Only bubble is offered. */
export const SEGMENT_CHART_OPTIONS: Array<{ id: SegmentChartKind; label: string }> = [
  { id: "bubble", label: "Addressable market × growth × sales" },
]

/** Portfolio grid sort order (§G9 user pref, #2068). "own-first" is the classic default. */
export type SegmentGridSort = "own-first" | "size" | "growth" | "alpha"

export const SEGMENT_GRID_SORT_OPTIONS: Array<{ id: SegmentGridSort; label: string }> = [
  { id: "own-first", label: "Mine first" },
  { id: "size", label: "Market size" },
  { id: "growth", label: "Growth (CAGR)" },
  { id: "alpha", label: "A–Z" },
]

/** KPI row density (§G9 user pref, #2068). Comfortable is the classic default. */
export type SegmentKpiDensity = "comfortable" | "compact"

export type SegmentAnalysisRunMode = "full" | "partial"

export type SegmentAnalysisBackendRunStatus =
  | "queued"
  | "processing"
  | "completed"
  | "failed"
  | "cancelled"

export type SegmentAnalysisDataSource = "backend" | "mock" | "mock-fallback"

/**
 * One configurable "addressable opportunity" line (G8). Tenant config defines the
 * set — generic/empty by default. Keyed so segment figures
 * (`segment.addressable[key]`) and the rendered KPIs/cards stay decoupled from any
 * customer-specific business term.
 */
export type AddressableLine = {
  key: string
  label: string
  sublabel?: string
}

/**
 * Default addressable lines — the frontend fallback when the portfolio payload
 * omits `presentation`. Empty by default (the module is generic/internal): the
 * KPI row degrades to the company-sales figure until an admin configures lines
 * (e.g. a signed customer's preset). Mirrors `_default_segment_analysis_config()`.
 */
export const DEFAULT_ADDRESSABLE_LINES: AddressableLine[] = []

export type SegmentAnalysisLoadStatus = "idle" | "loading" | "ready" | "error"

/** Customers-tab classification filter (§5.2 / G2). */
export type SegmentCustomerFilter = "all" | "customer" | "prospect"

export type SegmentDetailTab =
  | "market"
  | "customers"
  | "competitors"
  | "swot"
  | "actions"
  | "history"

export type SegmentMarket = {
  marketName: string
  strategicRole: string
  color: string
}

export type Segment = {
  id: string
  name: string
  owner: string
  /** AAD object id of the owning user; absent on legacy/seed segments (pre-F3). */
  ownerOid?: string | null
  /** Audit trail of ownership transfers (newest first); written on admin reassign. */
  ownerHistory?: SegmentOwnerChange[]
  /**
   * Retired (#3295 / Nolato feedback #19): new segments are created without
   * one; legacy segments may still carry a value, read unchanged.
   */
  priority?: string
  size: number
  cagr: number
  sales: number
  share: number
  /**
   * Per-user company favourites within this segment (§5.2/§5.8 / G2): AAD oid →
   * list of favourited company names. Rides the same segment-record persistence as
   * `favouriteBy`. Absent until a user favourites a company.
   */
  favouriteCompaniesBy?: Record<string, string[]>
  /** Persisted customer↔prospect reclassification overrides, keyed by company name. */
  companyClassifications?: Record<string, "customer" | "prospect">
  /**
   * Per-line addressable opportunity (MEUR), keyed by the addressable-line key
   * from tenant config (G8) — e.g. `{ plastics: 110, boxBuild: 32 }`. Master data:
   * present on demo/seed + imported segments, absent until imported for others
   * (the UI degrades to a generic figure when absent). Keyed, not fixed fields, so
   * it stays decoupled from any customer-specific business term.
   */
  addressable?: Record<string, number>
  marketName: string
  strategicRole: string
  description: string
  customers: string[]
  suppliers: string[]
  competitors: string[]
  attractiveness: number
  fit: number
  sparkline: number[]
  lastRunDate: string
  runStatus: SegmentRunStatus
  /** AAD object ids of users who favourited this segment (§5.8). */
  favouriteBy?: string[]
}

export type SegmentFilters = {
  market: string[]
  owner: string[]
  search: string
  favouritesOnly: boolean
}

export type SegmentPortfolioSummary = {
  segmentCount: number
  totalSales: number
  totalMarket: number
  averageGrowth: number
  averageFit: number
  /** Sum of each addressable line (by key) across the segments, MEUR (G1/G8). */
  addressableTotals: Record<string, number>
}

export type SegmentCreateDraft = {
  name: string
  market: string
  description: string
  keywords: string[]
  geo: string[]
  customers: string[]
  competitors: string[]
  sources: string[]
}

export type SegmentBuildInput = {
  id: string
  name: string
  marketName: string
  owner: string
  description: string
  customers: string[]
  competitors: string[]
  priority?: string
}

export type SegmentSource = {
  id?: string
  snippet?: string
  title: string
  url: string
}

export type SegmentClaimEvidence = {
  sourceId?: string
  title?: string
  url?: string
  file?: string
  quote?: string
  snippet?: string
  sourceDate?: string | null
  retrievedAt?: string | null
}

export type SegmentClaimFreshness = {
  label: "current" | "recent" | "aging" | "stale" | "unknown"
  sourceDate?: string | null
  retrievedAt?: string | null
}

export type SegmentClaimVerification = {
  status:
    | "verified"
    | "partially_verified"
    | "contradicted"
    | "unsupported"
    | "not_checked"
    | "manual_assertion"
  checkedAt?: string | null
  note?: string | null
  method?: string | null
}

export type SegmentClaimOrigin = "ai" | "manual" | "import" | "system"

export type SegmentClaimRecord = {
  claimId: string
  subjectKind: "segment" | "company"
  subjectId: string
  section: string
  fieldPath: string
  text: string
  origin: SegmentClaimOrigin
  status: "proposed" | "approved" | "rejected" | "superseded"
  evidence: SegmentClaimEvidence[]
  freshness: SegmentClaimFreshness
  verification: SegmentClaimVerification
  replacesClaimId?: string | null
  approvedBy?: string | null
  approvedAt?: string | null
  sourceRunId?: string | null
}

export type SegmentClaimRegistry = {
  subjectKind: "segment" | "company"
  subjectId: string
  claims: SegmentClaimRecord[]
  updatedAt?: string | null
}

export type SegmentReportEntity = {
  name: string
  changeSummary?: string
  rationale?: string
  flag?: string
  /** Rich entity detail (§5.2 / mockup tiles). All optional — the baseline omits them. */
  salesPotential?: "low" | "medium" | "high"
  assumedRevenue?: string
  assumedGrowth?: string
  /** Customer vs prospect classification (reclassify support). */
  classification?: "customer" | "prospect"
}

/** A dated market-news item with traceable source + confidence (§5.2 news feed). */
export type SegmentNewsItem = {
  headline: string
  summary?: string
  source?: string
  url?: string
  date?: string
  confidence?: "low" | "medium" | "high"
}

/** A recommended action — a plain string, or an object that deep-links to an entity. */
export type SegmentActionItem = { text: string; ref?: string }
export type SegmentRecommendedAction = string | SegmentActionItem

/** TOWS strategy matrix derived from the SWOT (mockup "Strategy conclusions"). */
export type SegmentStrategyMatrix = {
  so: string[]
  st: string[]
  wo: string[]
  wt: string[]
}

export type SegmentReport = {
  runId: string
  generatedAt: string
  periodCovered: {
    from: string
    to: string
  }
  summary: {
    executiveSummary: string[]
    keyChangesSinceLastRun: string[]
  }
  marketOverview: {
    marketSize: {
      value: number
      unit: string
      confidence: "low" | "medium" | "high"
      sources: string[]
    }
    marketGrowth: {
      cagr3yPct: number
      cagr5yPct: number
      confidence: "low" | "medium" | "high"
      sources: string[]
    }
    keyApplications: string[]
    macroTrends: string[]
    regulatoryFactors: string[]
    /** Dated market-news feed (§5.2). Omitted by the baseline; filled on a real run. */
    newsFeed?: SegmentNewsItem[]
    /**
     * Optional confidence + source links for the qualitative blocks (§5.2/§5.6). Shown
     * when the report provides them; the honest backend baseline omits them rather than
     * fabricate a confidence level for content it hasn't researched.
     */
    sections?: {
      keyApplications?: { confidence?: "low" | "medium" | "high"; sources?: string[] }
      macroTrends?: { confidence?: "low" | "medium" | "high"; sources?: string[] }
      regulatoryFactors?: { confidence?: "low" | "medium" | "high"; sources?: string[] }
    }
  }
  customerAnalysis: {
    existingKeyCustomers: SegmentReportEntity[]
    potentialNewCustomers: SegmentReportEntity[]
  }
  competitorAnalysis: {
    mainCompetitors: SegmentReportEntity[]
    newEntrants: SegmentReportEntity[]
    positionVsCompetition: {
      strengths: string[]
      weaknesses: string[]
    }
  }
  trendAndInnovation: {
    technologyTrends: string[]
    businessModelTrends: string[]
  }
  quantitativeIndicators: {
    companySharePct: number
    companyShareComment: string
    pricePressureIndicator: "low" | "medium" | "high"
    growthAttractivenessScore: number
    strategicFitScore: number
  }
  risksAndOpportunities: {
    risks: string[]
    opportunities: string[]
  }
  /**
   * Grounded claims + verification roll-up (10x M3.3). Present on a real run;
   * the baseline/mock omits it. `verification.lowTrust` counts cited claims
   * resting only on low-trust sources — surfaced as an approval-time warning.
   */
  grounded?: {
    claims?: CompanyClaim[]
    verification?: {
      verified: number
      contradicted: number
      unverified: number
      total: number
      verifiedRate: number
      lowTrust: number
    }
    /** Per-URL trust tier for badging source links (evidence isn't shipped in the report). */
    sourceTrustByUrl?: Record<string, SegmentSourceTier>
  }
  /** TOWS strategy conclusions (§ SWOT). Optional — baseline omits it. */
  strategyMatrix?: SegmentStrategyMatrix
  recommendedActions: {
    forSegmentOwner: SegmentRecommendedAction[]
    forGlobalSalesAdmin: SegmentRecommendedAction[]
  }
  history: SegmentRun[]
}

export type SegmentRun = {
  runId: string
  generatedAt: string
  changes: string[]
  /** Who approved/rejected this set of changes, when it came from a review decision (§5.4). */
  decidedBy?: string | null
  /** How the decision was authorised: owner / admin_override / auto (§5.4). */
  decidedVia?: "owner" | "admin_override" | "auto" | null
  /** AAD oid of who generated the proposal — run-by vs decided-by audit (§5.4). */
  runBy?: string | null
}

export type SegmentAgentStep = {
  id: string
  name: string
  icon: string
  messages: string[]
  durationMs: number
}

export type SegmentAnalysisPortfolioPayload = {
  agentSteps: SegmentAgentStep[]
  generatedAt: string
  markets: SegmentMarket[]
  reports: Record<string, SegmentReport>
  schemaVersion: 1
  segments: Segment[]
  source: "backend"
  /** AAD object id of the authenticated caller (for per-segment ownership checks). */
  currentUserOid?: string | null
  /** Display name of the authenticated caller (legacy ownership name-match). */
  currentUserName?: string | null
  /** Highest role the caller holds across the portfolio, resolved server-side (§5.7). */
  viewerRole?: SegmentAnalysisRole
  /** Count of pending (unapproved) changes per segment id (§5.4). */
  pendingCounts?: Record<string, number>
  /** Approved claim/evidence registry per segment id (#2074). */
  claimRegistries?: Record<string, SegmentClaimRegistry>
  /** Tenant-merged presentation config (G8): system+tenant merged server-side. */
  presentation?: { addressableLines: AddressableLine[] }
}

export type SegmentPendingChangeStatus = "pending" | "approved" | "rejected"

export type SegmentPendingChange = {
  changeId: string
  section: string
  label: string
  before: unknown
  after: unknown
  status: SegmentPendingChangeStatus
  decidedBy?: string | null
  decidedAt?: string | null
  target?: "report" | "segment"
  path?: string
  fieldPath?: string
  // Present on Phase-B roll-ups proposed from a company drill-down.
  origin?:
    | SegmentClaimOrigin
    | { type: string; companyId?: string; companyRunId?: string; sourceId?: string | null }
}

export type SegmentPendingChangesPayload = {
  segmentId: string
  runId?: string
  mode?: SegmentAnalysisRunMode
  createdAt?: string
  /** AAD oid of who generated this proposal — lets the UI flag self-review (§5.4). */
  runBy?: string | null
  changes: SegmentPendingChange[]
}

/** One ownership transfer in a segment's {@link Segment.ownerHistory} (newest first). */
export type SegmentOwnerChange = {
  at: string
  by: string
  fromOid?: string | null
  fromName?: string | null
  toOid?: string | null
  toName: string
}

export type SegmentAnalysisRunActivity = {
  id: string
  message: string
  status: SegmentAnalysisBackendRunStatus | "completed" | "failed"
}

export type SegmentAnalysisRunPayload = {
  activities: SegmentAnalysisRunActivity[]
  attemptId?: string | null
  completedAt?: string | null
  createdAt: string
  error?: string | null
  failureReason?: string | null
  heartbeatAt?: string | null
  mode: SegmentAnalysisRunMode
  /** Chat model catalog id used for this run (persisted on the run document). */
  modelId?: string | null
  /** Reasoning effort used for this run (when the model supports it). */
  reasoningEffort?: string | null
  report?: SegmentReport | null
  runId: string
  segmentId: string
  sourceCount: number
  sources: SegmentSource[]
  startedAt?: string | null
  status: SegmentAnalysisBackendRunStatus
  updatedAt: string
  // Layer-2 scope (present on company drill-down runs).
  subjectKind?: "segment" | "company"
  fromSegmentId?: string | null
}

// ── Layer 2: company drill-down ──────────────────────────────────────────────

export type SegmentCompany = {
  id: string
  name: string
  aliases?: string[]
  website?: string | null
  crmId?: string | null
  segmentIds?: string[]
  role?: "customer" | "competitor"
  /**
   * The company's CCP summary blurb, when it has a generated plan. Served with
   * the list so the Customers tab doesn't fetch a plan per company.
   */
  ccpSummary?: string
}

/**
 * Per-source trust tier (10x M3.3), computed server-side from the source URL:
 * `trusted` = official statistics/gov/established research, `standard` = ordinary
 * web, `low` = no resolvable/authoritative source.
 */
export type SegmentSourceTier = "trusted" | "standard" | "low"

export type CompanyClaimVerification = {
  status: "verified" | "contradicted" | "unverified"
  method?: string
  checkedAt?: string
  /** Best trust tier across the claim's cited sources (`low` when uncited). */
  sourceTrust?: SegmentSourceTier
}

export type CompanyClaim = {
  id: string
  text: string
  claimType?: string
  citations?: Array<{ sourceId: string; quote: string }>
  verification?: CompanyClaimVerification
}

export type CompanyReport = {
  companyId?: string
  companyName?: string
  profile?: {
    summary?: string
    hq?: string
    employees?: string
    revenue?: string
    ownership?: string
    sources?: string[]
  }
  recentMoves?: Array<{
    title?: string
    summary?: string
    date?: string
    flag?: string
    sources?: string[]
  }>
  companyFit?: {
    summary?: string
    strengths?: string[]
    gaps?: string[]
    confidence?: "low" | "medium" | "high"
  }
  relationship?: { status?: string; summary?: string }
  outreach?: { angle?: string; talkingPoints?: string[] }
  grounded?: {
    claims?: CompanyClaim[]
    verification?: {
      verified: number
      contradicted: number
      unverified: number
      total: number
      verifiedRate: number
    }
  }
  history?: Array<{ decidedBy?: string; generatedAt?: string; changes?: string[] }>
}

export type CompanyReportPayload = {
  company: SegmentCompany
  report: CompanyReport | null
}

export type CompanyPendingChangesPayload = {
  subjectId: string
  runId?: string
  mode?: SegmentAnalysisRunMode
  createdAt?: string
  changes: SegmentPendingChange[]
}

// ── Customer Centric Plan (CCP) + Competitor Plan (Nolato) ────────────────────
// Structured plan types live in `plan-types.ts` (kept separate to stay under the
// max-lines budget). Re-exported here so existing `../types` imports keep working.
export * from "./plan-types"
